import { useRef, useState } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';

import { Button } from '@nesso/ui/primitives/button';

import { type Service, serviceCatalogOptions } from '@/entities/service-catalog';
import { EditServiceRatesTable } from '@/features/practice/edit-service-rates';
import { SaveServiceSheet } from '@/features/practice/save-service';
import { PageTitle } from '@/shared/ui/text/page-title';
import { SettingsColumn } from '@/widgets/settings/settings-column';

import { SERVICE_HIGHLIGHT_MS } from '../config/services.config';
import { ServicesTable } from './services-page/services-table';

type SheetState = null | { mode: 'add' } | { mode: 'edit'; service: Service };

export function ServicesPage() {
    const { data: catalog } = useSuspenseQuery(serviceCatalogOptions());
    const [editingRates, setEditingRates] = useState(false);
    const [sheetOpen, setSheetOpen] = useState(false);
    // Kept populated through the sheet's close animation — Radix keeps `SheetContent` mounted
    // while it slides out, and resetting this to null on close would flip its title and fields to
    // add-mode mid-animation. Overwritten only when a new sheet is opened.
    const [sheet, setSheet] = useState<SheetState>(null);
    const [highlightedCode, setHighlightedCode] = useState<string | null>(null);
    const highlightTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

    function highlightRow(code: string) {
        clearTimeout(highlightTimeout.current);
        setHighlightedCode(code);
        highlightTimeout.current = setTimeout(() => setHighlightedCode(null), SERVICE_HIGHLIGHT_MS);
    }

    return (
        <SettingsColumn className="space-y-8">
            <div className="flex items-center justify-between">
                <PageTitle>Services</PageTitle>
                <div className="flex items-center gap-2">
                    {editingRates ? (
                        <Button variant="ghost" size="sm" onClick={() => setEditingRates(false)}>
                            Cancel
                        </Button>
                    ) : (
                        <>
                            <Button variant="ghost" size="sm" onClick={() => setEditingRates(true)}>
                                Edit All Rates
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setSheet({ mode: 'add' });
                                    setSheetOpen(true);
                                }}
                            >
                                Add Service
                            </Button>
                        </>
                    )}
                </div>
            </div>
            {editingRates ? (
                <EditServiceRatesTable
                    services={catalog.services}
                    onSaved={() => setEditingRates(false)}
                />
            ) : (
                <ServicesTable
                    services={catalog.services}
                    highlightedCode={highlightedCode}
                    onEdit={(service) => {
                        setSheet({ mode: 'edit', service });
                        setSheetOpen(true);
                    }}
                />
            )}
            <SaveServiceSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                service={sheet?.mode === 'edit' ? sheet.service : undefined}
                onSuccess={(code) => {
                    const wasAdding = sheet?.mode === 'add';
                    setSheetOpen(false);
                    if (wasAdding) highlightRow(code);
                }}
            />
        </SettingsColumn>
    );
}
